<template>
  <div class="pd-product-card">
    <div class="pd-product-link-wrapper">
      <!-- Image Container with Hover Effect -->
      <div class="pd-product-image-wrapper">
        <router-link :to="{ name: 'product', params: { productId: product.id }}" class="pd-product-image-link"
          @mouseenter.native="isHovering = true"
          @mouseleave.native="isHovering = false">
          <!-- Base Image (First Image) -->
          <img
            class="pd-product-image pd-product-image-base"
            :src="product.image"
            :alt="`${product.name} - ${product.category || '產品圖片'}`"
          />
          <!-- Overlay Image (Second Image) - Fades in on hover -->
          <img
            v-if="hasMultipleImages"
            class="pd-product-image pd-product-image-overlay"
            :class="{ 'pd-image-visible': isHovering }"
            :src="secondImage"
            :alt="`${product.name} - ${product.category || '產品圖片'} 2`"
          />
        </router-link>

        <!-- Sold Out Badge -->
        <div v-if="isSoldOut" class="pd-sold-out-badge">
          售完
        </div>

        <!-- Quick Add to Cart Button (now positioned over image) -->
        <button
          v-if="!isSoldOut"
          class="pd-quick-add"
          aria-label="快速加入購物車"
          @click.stop.prevent="handleAddToCart"
        >
          加入購物車
        </button>
      </div>

      <!-- Product Info -->
      <router-link :to="{ name: 'product', params: { productId: product.id }}" class="pd-product-info-link">
        <div class="pd-product-info">
          <h3 class="pd-product-name">{{ product.name }}</h3>
          <div class="pd-product-price">
            <span class="pd-price-current">NT${{ formatPrice(product.price) }}</span>
          </div>
          <!-- Color Variants -->
          <div v-if="availableColors.length > 1" class="pd-color-dots">
            <span
              v-for="color in availableColors"
              :key="color"
              class="pd-color-dot"
              :title="color"
            ></span>
          </div>
        </div>
      </router-link>
    </div>

    <!-- Variant Selection Modal -->
    <ProductVariantModal
      :is-visible="showModal"
      :product="product"
      @close="showModal = false"
      @add-to-cart="handleModalAddToCart"
    />
  </div>
</template>

<script>
import { mapMutations } from 'vuex'
import ProductVariantModal from './ProductVariantModal.vue'

export default {
  components: {
    ProductVariantModal
  },
  props: {
    // The parent component must now pass the product WITH its variants
    product: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      isHovering: false,
      showModal: false
    }
  },
  computed: {
    variants () {
      // Supabase might name the related table 'product_variants'
      return this.product.product_variants || []
    },
    isSoldOut () {
      if (!this.variants || this.variants.length === 0) {
        // If no variants are defined for a product, it's effectively unavailable.
        return true
      }
      const totalInventory = this.variants.reduce((sum, variant) => sum + variant.inventory, 0)
      return totalInventory === 0
    },
    hasMultipleImages () {
      return this.product.images && this.product.images.length > 0
    },
    secondImage () {
      if (this.hasMultipleImages) {
        return this.product.images[0]
      }
      return this.product.image
    },
    availableColors () {
      if (!this.variants || this.variants.length === 0) return []
      const colors = [...new Set(this.variants.map(v => v.color).filter(Boolean))]
      return colors
    }
  },
  methods: {
    ...mapMutations(['addCartItem']),
    handleAddToCart () {
      // Open modal for variant selection
      this.showModal = true
    },
    handleModalAddToCart (itemToAdd) {
      // Add item from modal
      this.addCartItem(itemToAdd)

      // Open the side cart (removed toast notification)
      this.$root.$emit('toggleSideCart')
    },
    formatPrice (price) {
      if (price === undefined || price === null) return ''
      return new Intl.NumberFormat('zh-TW').format(price)
    }
  }
}
</script>

<style scoped>
.pd-product-card {
  position: relative;
  background-color: var(--pd-white);
  transition: transform var(--transition-normal);
  overflow: hidden; /* Hide overflowing elements */
}

/* Image Wrapper */
.pd-product-image-wrapper {
  position: relative;
  width: 100%;
  padding-bottom: 125%; /* 4:5 aspect ratio */
  overflow: hidden;
  background-color: var(--pd-gray-light);
}

.pd-product-image-link {
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.pd-product-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transition: transform var(--transition-slow) ease, opacity var(--transition-slow) ease;
}

.pd-product-image-base {
  z-index: 1;
}

.pd-product-image-overlay {
  z-index: 2;
  opacity: 0;
}

.pd-product-image-overlay.pd-image-visible {
  opacity: 1;
}

.pd-product-card:hover .pd-product-image {
  transform: scale(1.05);
}

/* Sold Out Badge */
.pd-sold-out-badge {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: var(--pd-gray-sold-out);
  color: var(--pd-white);
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  z-index: 10;
}

/* Product Info */
.pd-product-info-link {
  text-decoration: none;
  color: inherit;
}

.pd-product-info {
  padding: 1rem 0.5rem;
  text-align: center;
}

.pd-product-name {
  font-size: 0.9375rem;
  font-weight: 400;
  margin-bottom: 0.5rem;
  color: var(--pd-black);
  letter-spacing: 0.02em;
  line-height: 1.5;
  /* Allow multi-line for longer product names */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 2.8em;
}

.pd-product-price {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.75rem;
}

.pd-price-current {
  font-size: 0.875rem;
  font-weight: 400;
  color: #666;
}

/* Color Dots */
.pd-color-dots {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.pd-color-dot {
  width: 16px;
  height: 16px;
  border: 1px solid #ddd;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.pd-color-dot:nth-child(1) {
  background-color: #000;
}

.pd-color-dot:nth-child(2) {
  background-color: #d3d3d3;
}

.pd-color-dot:nth-child(3) {
  background-color: #fff;
}

.pd-color-dot:nth-child(4) {
  background-color: #8b4513;
}

.pd-color-dot:hover {
  transform: scale(1.1);
}

/* Quick Add Button */
.pd-quick-add {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 1rem;
  background-color: rgba(0, 0, 0, 0.75);
  color: var(--pd-white);
  border: none;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.3s ease;
  opacity: 0;
  transform: translateY(100%); /* Start hidden below the wrapper */
  z-index: 5;
}

/* Show button on image wrapper hover */
.pd-product-image-wrapper:hover .pd-quick-add {
  opacity: 1;
  transform: translateY(0);
}

.pd-quick-add:hover {
  background-color: var(--pd-black);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  /* Hide the quick add button on mobile - users will use product detail page */
  .pd-quick-add {
    display: none;
  }

  .pd-product-info {
    padding: 0.75rem 0.25rem;
  }

  .pd-product-name {
    font-size: 0.8125rem;
    min-height: 2.4em;
  }

  .pd-price-current {
    font-size: 0.8125rem;
  }

  .pd-color-dot {
    width: 14px;
    height: 14px;
  }
}
</style>
